// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Delivery zones  (where ZenFresh delivers — drawn by staff, enforced when ordering)
// Client for GET /public/zones (the ACTIVE zones + whether ordering is limited to them) and the staff endpoints /admin/zones, plus the geometry the map screens need: point-in-polygon
// (the same rule the backend applies in domain/zone.py), area and perimeter for the live readout, and a GeoJSON export. Once one zone is active the backend refuses an order whose delivery pin
// is outside every active zone or whose subtotal is under the zone's minimum; checkout and the address form use these helpers to say so BEFORE the customer pays.
// Neighbours: hooks/useZones, pages/Checkout, components/delivery/AddressForm + PinMap, pages/admin/AdminZones, utils/adminApi.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed, apiGet } from "./api";

export interface LatLng { lat: number; lng: number }
export interface PublicZone { id: string; name: string; polygon: LatLng[]; minOrder: number; deliveryFee: number | null; freeAbove: number | null }
export interface PublicZones { enforced: boolean; zones: PublicZone[] }
export interface Zone extends PublicZone { active: boolean; note: string | null; areaKm2: number; perimeterKm: number; updatedBy: string; updatedAt: string }
export interface ZoneInput { name: string; active: boolean; polygon: LatLng[]; minOrder: number; deliveryFee: number | null; freeAbove: number | null; note: string }

export const fetchPublicZones = () => apiGet<PublicZones>("/public/zones");
export const fetchAdminZones = () => apiAuthed<Zone[]>("GET", "/admin/zones");
export const saveZone = (id: string, input: ZoneInput) => apiAuthed<Zone>("PUT", `/admin/zones/${encodeURIComponent(id)}`, input);
export const deleteZone = (id: string) => apiAuthed<{ deleted: boolean }>("DELETE", `/admin/zones/${encodeURIComponent(id)}`);

export const MIN_VERTICES = 3;
export const MAX_VERTICES = 60;
const EARTH_KM = 6371.0088;
const rad = (d: number) => (d * Math.PI) / 180;

/** Ray casting: is the point inside the polygon? (Mirrors backend domain/zone.point_in_polygon.) */
export function pointInPolygon(p: LatLng, polygon: LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i], b = polygon[j];
    if ((a.lat > p.lat) !== (b.lat > p.lat) && p.lng < ((b.lng - a.lng) * (p.lat - a.lat)) / (b.lat - a.lat) + a.lng) inside = !inside;
  }
  return inside;
}
export const findActiveZone = (zones: PublicZone[], p: LatLng) => zones.find((z) => pointInPolygon(p, z.polygon)) ?? null;

export function areaKm2(polygon: LatLng[]): number {
  if (polygon.length < 3) return 0;
  const lat0 = rad(polygon.reduce((n, p) => n + p.lat, 0) / polygon.length);
  const pts = polygon.map((p) => [rad(p.lng) * Math.cos(lat0) * EARTH_KM, rad(p.lat) * EARTH_KM]);
  let total = 0;
  for (let i = 0; i < pts.length; i++) { const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length]; total += x1 * y2 - x2 * y1; }
  return Math.abs(total) / 2;
}
export function perimeterKm(polygon: LatLng[]): number {
  let total = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length];
    const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
    total += 2 * EARTH_KM * Math.asin(Math.sqrt(h));
  }
  return total;
}

const orient = (a: LatLng, b: LatLng, c: LatLng) => (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
const cross = (a: LatLng, b: LatLng, c: LatLng, d: LatLng) => orient(a, b, c) * orient(a, b, d) < 0 && orient(c, d, a) * orient(c, d, b) < 0;
/** Why this outline can't be saved as a zone (the API checks the same things), or null. */
export function outlineProblem(polygon: LatLng[]): string | null {
  const n = polygon.length;
  if (n < MIN_VERTICES) return `Add at least ${MIN_VERTICES} points (you have ${n}).`;
  if (n > MAX_VERTICES) return `A zone can have at most ${MAX_VERTICES} points.`;
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    if (j === i + 1 || (i === 0 && j === n - 1)) continue;
    if (cross(polygon[i], polygon[(i + 1) % n], polygon[j], polygon[(j + 1) % n])) return "The outline crosses itself. Drag a point so the edges don't cross.";
  }
  return areaKm2(polygon) < 0.0001 ? "The points are in a straight line; the zone has no area." : null;
}

/** GeoJSON (RFC 7946: [lng, lat], ring closed) for every zone given. */
export function toGeoJson(zones: Pick<Zone, "id" | "name" | "active" | "polygon" | "minOrder" | "deliveryFee" | "freeAbove">[]) {
  return {
    type: "FeatureCollection",
    features: zones.map((z) => ({
      type: "Feature",
      properties: { id: z.id, name: z.name, active: z.active, minOrder: z.minOrder, deliveryFee: z.deliveryFee, freeAbove: z.freeAbove },
      geometry: { type: "Polygon", coordinates: [[...z.polygon, z.polygon[0]].map((p) => [p.lng, p.lat])] },
    })),
  };
}
